<template>
  <div
    id="business_address"
    class="flex flex-col gap-6 text-[13px] secondary_color proxima_nova"
  >
    <div
      v-if="countriesRequiringBusinessName.includes(countryCode)"
      id="business_name"
      class="flex flex-col gap-1"
    >
      <span>Business Name*</span>
      <a-input
        v-model:value="localFormData.businessName"
        class="input_medium"
        placeholder="Enter business name"
      />
    </div>

    <div
      v-if="countryCode === countryMapping.CH"
      id="enterprise_id_number"
      class="flex flex-col gap-1"
    >
      <span>Enterprise ID Number*</span>
      <a-input
        v-model:value="localFormData.enterpriseIdNumber"
        class="input_medium"
        placeholder="Enter enterprise ID number"
      />
    </div>

    <div id="business_address_line_1" class="flex flex-col gap-1">
      <span>Business Address*</span>
      <a-input
        v-model:value="localFormData.businessAddressLine1"
        class="input_medium"
        placeholder="Enter business address"
      />
    </div>

    <div id="business_address_line_2" class="flex flex-col gap-1">
      <span>Business Address Line 2</span>
      <a-input
        v-model:value="localFormData.businessAddressLine2"
        class="input_medium"
        placeholder="Enter business address"
      />
    </div>

    <div id="city" class="flex flex-col gap-1">
      <span>City*</span>
      <a-input
        v-model:value="localFormData.city"
        class="input_medium"
        placeholder="Enter city"
      />
    </div>

    <div id="state" class="flex flex-col gap-1">
      <span>State/Province*</span>
      <a-input
        v-model:value="localFormData.state"
        class="input_medium"
        placeholder="Enter state/province"
      />
    </div>

    <div id="postal_code" class="flex flex-col gap-1">
      <span>Zip/Postal Code*</span>
      <a-input
        class="input_medium"
        v-model:value="localFormData.postalCode"
        placeholder="Enter zip/postal code"
      />
    </div>

    <div id="country" class="flex flex-col gap-1">
      <span>Country/Region</span>
      <a-select
        v-model:value="selectedCountryCode"
        class="w-full"
        placeholder="Select a country"
        :disabled="countryCode !== countryMapping.UK"
        show-search
        :filter-option="false"
        @search="handleCountrySearch"
        @change="handleCountryChange"
      >
        <template #suffixIcon>
          <ThemeImage
            class="size-4 opacity-40"
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
          />
        </template>

        <a-select-option
          v-for="(country, index) in filteredCountries"
          :key="`${country.countryCode}_country_${index}`"
          :value="country.countryCode"
          :label="country.label"
        >
          <div class="flex flex-row items-center gap-2">
            <img
              v-if="country.flag"
              :src="country.flag"
              :alt="`${country.label} flag`"
              class="size-4 object-cover rounded-full border-radius-full"
            />
            {{ country.label }}
          </div>
        </a-select-option>
      </a-select>
    </div>

    <a-button
      class="primary_medium self-end"
      :disabled="!isStepValid"
      :loading="submitting"
      @click="handleContinue"
    >
      {{ submitting ? "Continuing" : "Continue" }}
    </a-button>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { message } from "ant-design-vue";
import { capitalizeFirstLetter, fetchCountries } from "@/src/utils/utils.js";
import {
  countryMapping,
  countryDetails,
} from "@/src/constants/phoneNumberConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import CallService from "@/src/api/CallService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";

const route = useRoute();

const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
});

const countryCode = computed(() => route?.params?.country?.toUpperCase() || "");
const countriesRequiringBusinessName = [countryMapping.NZ, countryMapping.CH];

const emit = defineEmits(["update:formData", "next-step"]);

const localFormData = ref({ ...props.formData });
const submitting = ref(false);
const selectedCountryCode = ref(countryCode.value);
const searchText = ref("");

const allCountries = ref([]);
const countriesLoading = ref(false);

const filteredCountries = computed(() => {
  if (searchText.value) {
    return allCountries.value?.filter(country => 
      country.label.toLowerCase().includes(searchText.value.toLowerCase())
    );
  }
  return allCountries.value;
});

const handleCountrySearch = (value) => {
  searchText.value = value;
};

const handleCountryChange = (newCountryCode) => {
  selectedCountryCode.value = newCountryCode;
  const selectedCountry = allCountries.value.find(c => c.countryCode === newCountryCode);
  if (selectedCountry) {
    localFormData.value.isoCountry = selectedCountry.countryCode;
  }
};


const isStepValid = computed(() => {
  const basicFieldsValid = Boolean(
    localFormData.value.businessAddressLine1 &&
      localFormData.value.city &&
      localFormData.value.state &&
      localFormData.value.postalCode
  );
  
  const businessNameValid = !countriesRequiringBusinessName.includes(countryCode.value) || 
    localFormData.value.businessName;
  
  const switzerlandEnterpriseIdValid = countryCode.value !== countryMapping.CH || 
    localFormData.value.enterpriseIdNumber;
  
  return basicFieldsValid && businessNameValid && switzerlandEnterpriseIdValid;
});

async function toCreateAddress() {
  try {
    submitting.value = true;

    const payload = {
      id: localFormData.value?.infoID,
      business_address_line1: localFormData.value?.businessAddressLine1,
      business_address_line2: localFormData.value?.businessAddressLine2,
      city: localFormData.value?.city,
      state: localFormData.value?.state,
      postal_code: localFormData.value?.postalCode,
      iso_country: localFormData.value?.isoCountry,
    };

    if (countriesRequiringBusinessName.includes(countryCode.value)) {
      payload.business_name = localFormData.value?.businessName;
      payload.country_code = countryCode.value;
      payload.id = payload.id || null;
    }

    if (countryCode.value === countryMapping.CH) {
      payload.enterprise_id_number = localFormData.value?.enterpriseIdNumber;
    }


    const response = await CallService.createAddress(payload);
    if (response?.success) {
      message.success("Business Address stored successfully!");
      emit("update:formData", { ...localFormData.value, infoID: response?.id });
      return true;
    }
    return false;
  } catch (error) {
    console.error("An error occurred while creating address: ", error);
    message.error(
      capitalizeFirstLetter(
        error?.response?.data?.error || "Failed to store business address",
      ),
    );
    return false;
  } finally {
    submitting.value = false;
  }
}

async function handleContinue() {
  try {
    if (!isStepValid.value) return;

    emit("update:formData", { ...localFormData.value });

    const success = await toCreateAddress();
    if (success) {
      emit("next-step");
    }
  } catch (error) {
    console.error("An error occurred while handling continue: ", error);
  }
}

watch(
  () => props.formData,
  (newVal) => {
    localFormData.value = { ...newVal };
  },
  { deep: true },
);

watch(
  () => countryCode.value,
  (newCountryCode) => {
    if (newCountryCode && countryDetails[newCountryCode]) {
        selectedCountryCode.value = newCountryCode;
        localFormData.value.isoCountry = countryDetails[newCountryCode].countryCode;
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await fetchCountries(allCountries, countriesLoading);
  selectedCountryCode.value = countryCode.value;
  localFormData.value.isoCountry = countryDetails[countryCode.value]?.countryCode || "";
});
</script>
